import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./BigCommerceInvoiceEditPage.module.css";
import {
  fetchProductOptions,
  getBigCommerceInvoiceById,
  regenerateAndDownloadInvoice,
} from "../../api/api";

export default function BigCommerceInvoiceEditPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const { invoiceId } = useParams();
  const [invoiceData, setInvoiceData] = useState(null);
  const [items, setItems] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const navigate = useNavigate();
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const fetchInvoice = async () => {
      const invoiceData = await getBigCommerceInvoiceById(invoiceId);
      setInvoiceData(invoiceData.invoice);
      setItems(invoiceData.items);
    };
    fetchInvoice();
  }, [invoiceId]);

  const handleRemoveItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDownload = async () => {
    try {
      const blob = await regenerateAndDownloadInvoice(invoiceId, items);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${invoiceData.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
      setNotice("✅ Invoice downloaded successfully!");
      setTimeout(() => setNotice(""), 3500);
    } catch (err) {
      console.error("Download failed:", err);
      setNotice("❌ Failed to regenerate or download invoice.");
      setTimeout(() => setNotice(""), 5000);
    }
  };

  const handleAddItem = async () => {
    const product = productOptions.find(
      (p) => p.id === Number(selectedProductId)
    );
    if (!product) {
      alert("Please select a product");
      return;
    }

    const total = parseFloat(product.price);
    const unitPrice = total / 1.05;
    const vatAmount = total - unitPrice;

    const newItem = {
      product_id: product.id,
      item: product.sku || `P${product.id}`,
      description: product.name,
      quantity: 1,
      unit_price: unitPrice.toFixed(2),
      vat_amount: vatAmount.toFixed(2),
      total: total.toFixed(2),
    };

    setItems((prev) => [...prev, newItem]);
    setShowAddModal(false);
    setSearchTerm("");
    setSelectedProductId("");
  };

  const handleToggleDiscount = () => {
    const hasDiscount = items.some((i) => i.item === "DISCOUNT");
    if (hasDiscount) {
      setItems((prev) => prev.filter((i) => i.item !== "DISCOUNT"));
    } else {
      const discountAmount = 50.0;
      const unitPrice = -(discountAmount / 1.05);
      const vatAmount = -discountAmount + unitPrice;

      setItems((prev) => [
        ...prev,
        {
          item: "DISCOUNT",
          description: "Discount",
          quantity: 1,
          unit_price: unitPrice.toFixed(2),
          vat_amount: vatAmount.toFixed(2),
          total: (-discountAmount).toFixed(2),
        },
      ]);
    }
  };

  const handleToggleShipping = () => {
    const hasShipping = items.some((i) => i.item === "SHIPPING");
    if (hasShipping) {
      setItems((prev) => prev.filter((i) => i.item !== "SHIPPING"));
    } else {
      setItems((prev) => [
        ...prev,
        {
          item: "SHIPPING",
          description: "Shipping Charge",
          quantity: 1,
          unit_price: "30.00",
          vat_amount: "1.50",
          total: "31.50",
        },
      ]);
    }
  };

  const openAddModal = async () => {
    const products = await fetchProductOptions();
    setProductOptions(products);
    setFilteredOptions(products);
    setShowAddModal(true);
  };

  const handleSearchChange = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = productOptions.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        p.sku.toLowerCase().includes(term)
    );
    setFilteredOptions(filtered);
  };

  if (!invoiceData) return <p>Loading...</p>;

  return (
    <div className={styles.container}>
      <button className={styles.backButton} onClick={() => navigate(-1)}>
        ← Back
      </button>

      <h1 className={styles.header}>
        Edit Invoice #{invoiceData.invoice_number}
      </h1>
      {notice && <div className={styles.notice}>{notice}</div>}
      <div className={styles.section}>
        <p>
          <strong>Customer:</strong> {invoiceData.customer_name}
        </p>
        <p>
          <strong>Order ID:</strong> {invoiceData.order_id}
        </p>
        <p>
          <strong>Created At:</strong>{" "}
          {new Date(invoiceData.created_at).toLocaleString()}
        </p>
      </div>

      <h2>Items</h2>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Item</th>
            <th className={styles.th}>Description</th>
            <th className={styles.th}>Qty</th>
            <th className={styles.th}>Unit Price</th>
            <th className={styles.th}>VAT</th>
            <th className={styles.th}>Total</th>
            <th className={styles.th}>Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={idx}>
              <td className={styles.td}>{item.item}</td>
              <td className={styles.td}>{item.description}</td>
              <td className={styles.td}>
                <input
                  type="number"
                  value={item.quantity}
                  min="1"
                  className={styles.inputField}
                  onChange={(e) => {
                    const updatedItems = [...items];
                    updatedItems[idx].quantity = parseInt(e.target.value, 10);
                    updatedItems[idx].total = (
                      updatedItems[idx].quantity *
                      updatedItems[idx].unit_price *
                      1.05
                    ).toFixed(2);
                    updatedItems[idx].vat_amount = (
                      updatedItems[idx].quantity *
                      updatedItems[idx].unit_price *
                      0.05
                    ).toFixed(2);
                    setItems(updatedItems);
                  }}
                />
              </td>

              <td className={styles.td}>
                <input
                  type="number"
                  value={item.unit_price}
                  step="0.01"
                  className={styles.inputField}
                  disabled={item.item === "DISCOUNT"}
                  onChange={(e) => {
                    if (item.item === "DISCOUNT") return;
                    const updatedItems = [...items];
                    updatedItems[idx].unit_price = parseFloat(e.target.value);
                    updatedItems[idx].total = (
                      updatedItems[idx].quantity *
                      updatedItems[idx].unit_price *
                      1.05
                    ).toFixed(2);
                    updatedItems[idx].vat_amount = (
                      updatedItems[idx].quantity *
                      updatedItems[idx].unit_price *
                      0.05
                    ).toFixed(2);
                    setItems(updatedItems);
                  }}
                />
              </td>

              <td className={styles.td}>
                <input
                  type="number"
                  value={item.vat_amount}
                  step="0.01"
                  className={styles.inputField}
                  disabled={item.item === "DISCOUNT"}
                  onChange={(e) => {
                    if (item.item === "DISCOUNT") return;
                    const updatedItems = [...items];
                    updatedItems[idx].vat_amount = parseFloat(e.target.value);
                    updatedItems[idx].total = (
                      parseFloat(updatedItems[idx].unit_price) *
                        updatedItems[idx].quantity +
                      parseFloat(updatedItems[idx].vat_amount)
                    ).toFixed(2);
                    setItems(updatedItems);
                  }}
                />
              </td>

              <td className={styles.td}>
                {item.item === "DISCOUNT" ? (
                  <input
                    type="number"
                    value={item.total}
                    step="0.01"
                    className={styles.inputField}
                    onChange={(e) => {
                      const updatedItems = [...items];
                      const newTotal = parseFloat(e.target.value);

                      updatedItems[idx].total = newTotal.toFixed(2);
                      updatedItems[idx].unit_price = (newTotal / 1.05).toFixed(
                        2
                      );
                      updatedItems[idx].vat_amount = (
                        newTotal -
                        newTotal / 1.05
                      ).toFixed(2);

                      setItems(updatedItems);
                    }}
                  />
                ) : (
                  item.total
                )}
              </td>

              <td className={styles.td}>
                <span
                  className={styles.actions}
                  onClick={() => handleRemoveItem(idx)}
                >
                  ❌
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button className={styles.button} onClick={openAddModal}>
        ➕ Add Item
      </button>
      <button className={styles.button} onClick={handleToggleShipping}>
        🚚 Toggle Shipping
      </button>
      <button className={styles.button} onClick={handleToggleDiscount}>
        💸 Toggle Discount
      </button>
      <button className={styles.button} onClick={handleDownload}>
        ⬇️ Download PDF
      </button>

      {showAddModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowAddModal(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Select Product</h3>
            <input
              type="text"
              placeholder="Search by name or SKU..."
              className={styles.searchInput}
              value={searchTerm}
              onChange={handleSearchChange}
            />

            <select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(Number(e.target.value))}
              className={styles.selectBox}
            >
              <option value="">-- Select --</option>
              {filteredOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.sku}) - AED {p.price}
                </option>
              ))}
            </select>

            <br />
            <button className={styles.button} onClick={handleAddItem}>
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
